/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MentorFeedback } from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import { VersionHelper } from '@app/app/shared/helpers/version.helper';
import { SingleMediaPlaybackService } from '@app/app/shared/services/single-media-playback.service';
import {
  EditorContent,
  ExternalContentSummary,
  FileEditorContent,
  ImageUploadContent,
  MediaType,
  MediaUploadData,
  MediaUrlData,
  SingleUrlContent,
  UserPlaylistSubmissionSummary,
} from '../../models';
import { ChartLearner } from '../../models/editor/chart-content.model';
import { MediaDisplayMode } from '../../models/editor/media-content.model';

/** Media types that can be presented as a download card instead of an inline embed. */
const DOWNLOAD_CARD_MEDIA_TYPES: ReadonlySet<MediaType> = new Set<MediaType>(['IMAGE', 'AUDIO', 'DOCUMENT']);

@Component({
  selector: 'ptl-resource-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourcePreviewComponent implements OnChanges {
  @Input() previewContent: EditorContent[] = [];
  @Input() userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary;
  @Input() userPlaylistAllMentorsFeedback?: MentorFeedback[];
  @Input() hideTextFormButtons = false;
  @Input() folioAttachmentPreviewView = false;
  @Input() isCollectorTemplateForm = false;
  @Input() canEditCollectorTemplateForm = false;
  @Input() includeSelfFeedback: boolean;
  @Input() withTruncateLines = false;
  @Input() collage = false;
  @Input() learner: ChartLearner;

  @HostBinding('class.v3') isV3Enabled = VersionHelper.isV3Enabled();

  constructor(
    private cd: ChangeDetectorRef,
    singleMediaPlayback: SingleMediaPlaybackService,
  ) {
    if (this.isV3Enabled) {
      singleMediaPlayback.start();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.previewContent) {
      this.previewContent = changes.previewContent.currentValue;
    }
    if (changes.userPlaylistSubmissionSummary && changes.userPlaylistSubmissionSummary.currentValue) {
      this.cd.detectChanges();
    }
  }

  isContentHtmlPreview(content: EditorContent): boolean {
    return (
      this.isNonImageMediaUrl(content) ||
      content.type === 'PARAGRAPH' ||
      content.type === 'SEPARATOR' ||
      content.type === 'EMBED_CODE' ||
      content.type === 'TABLE'
    );
  }

  isContentImagePreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'IMAGE';
  }

  isContentStreamVideoPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'STREAM_VIDEO';
  }

  isContentVideoPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'VIDEO';
  }

  isContentAudioPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'AUDIO';
  }

  isContentDocumentPreview(content: EditorContent): boolean {
    return content.type === 'MEDIA_UPLOAD' && (content as MediaUploadData).mediaType === 'DOCUMENT';
  }

  /**
   * True when the author chose to present this upload as a downloadable file rather than an inline
   * embed. Content authored before the choice existed has no `displayMode` and keeps embedding.
   */
  isContentDownloadCard(content: EditorContent): boolean {
    const upload = content as MediaUploadData;
    return (
      content.type === 'MEDIA_UPLOAD' && upload.displayMode === MediaDisplayMode.DOWNLOAD && DOWNLOAD_CARD_MEDIA_TYPES.has(upload.mediaType)
    );
  }

  /**
   * The URL of the file itself. Images keep their upload URL under `content.original` and use the
   * top-level `url` for an optional author-supplied hyperlink, so they must not read `url` here.
   */
  getDownloadCardUrl(content: EditorContent): string {
    const upload = content as MediaUploadData;
    if (upload.mediaType === 'IMAGE') {
      return (upload.content as ImageUploadContent)?.original ?? '';
    }
    return (upload.content as SingleUrlContent)?.url ?? upload.url ?? '';
  }

  getDownloadCardFileName(content: EditorContent): string | null {
    return (content as MediaUploadData).fileName || (content as MediaUploadData).name || null;
  }

  getDownloadCardCaption(content: EditorContent): string | null {
    return (content as MediaUploadData).caption || null;
  }

  trackByFn(index: number, { uid }: EditorContent) {
    return uid || index;
  }

  getUrl(content: EditorContent): string {
    return (content as MediaUrlData)?.url ?? content?.content?.url;
  }

  getText(content: EditorContent): string {
    return (
      (content as FileEditorContent)?.caption ||
      (content as MediaUploadData)?.name ||
      (content as ExternalContentSummary)?.title ||
      this.getUrl(content)
    );
  }

  private isNonImageMediaUrl(content: EditorContent): boolean {
    return content.type === 'MEDIA_URL' && this.isNotImage(content as MediaUrlData);
  }

  private isNotImage(content: MediaUrlData): boolean {
    const mediaType: MediaType = content.mediaType;
    return mediaType !== 'IMAGE';
  }

  getImageContents(): EditorContent[] {
    return this.previewContent.filter((content) => this.isContentImagePreview(content) && !this.isContentDownloadCard(content));
  }

  shouldShowCollage(): boolean {
    return this.collage && this.getImageContents().length > 1;
  }

  getCollageClass(): string {
    const imageCount = this.getImageContents().length;
    if (imageCount <= 1) return '';
    if (imageCount <= 5) return `collage-${imageCount}`;
    return 'collage-6-plus';
  }

  getDisplayedImages(): EditorContent[] {
    const images = this.getImageContents();
    if (images.length <= 5) return images;
    return images.slice(0, 5);
  }

  getRemainingCount(): number {
    const images = this.getImageContents();
    return Math.max(0, images.length - 5);
  }

  getFirstImageIndex(): number {
    return this.previewContent.findIndex((content) => this.isContentImagePreview(content) && !this.isContentDownloadCard(content));
  }
}
